import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectActiveFundingSourceOperation,
  selectClient,
  selectSession,
  selectSessionAdvisor,
} from '@client/model/selectors';
import { putMultipleFundingSourceForClientByClientIdRequest } from '@client/model';
import { SessionType } from '@shared';
import { useCareHasStarted } from '@/shared/hooks/use-care-has-started';

import {
  ASSET_TYPES,
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  isIncomeStreamAssetTypeId,
  type AssetTypeId,
} from '@/features/guided-financial-planning/config';

// ─────────────────────────────────────────────────────────────
// SCHEMA
// ─────────────────────────────────────────────────────────────

const selfFundingFormSchema = z.object({
  existingAssetsValue: z.number({ error: 'Required' }).min(0, 'Must be 0 or greater'),
  yearlyContribution: z.number().nullable(),
  annualRateOfReturn: z.number({ error: 'Required' }),
  gainsTaxRate: z.number({ error: 'Required' }),
  contributionStopYear: z.number().nullable().optional(),
  hasDrawdown: z.boolean().default(false),
  drawdownStartAtYear: z.number().nullable().optional(),
  drawdownEndAtYear: z.number().nullable().optional(),
  drawdownAmount: z.number().min(0).nullable().optional(),
  drawdownAmountInterval: z.enum(['monthly', 'yearly']).default('yearly'),
  incomeReferenceAge: z.number().int().min(0).max(120).nullable().optional(),
});

export type SelfFundingFormValues = z.infer<typeof selfFundingFormSchema>;
export type SelfFundingFormInput = z.input<typeof selfFundingFormSchema>;

export function isSelfFundingFormSavable(values: unknown): boolean {
  return selfFundingFormSchema.safeParse(values).success;
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

function getAssetLabel(assetTypeId: AssetTypeId): string {
  return ASSET_TYPES.find(asset => asset.id === assetTypeId)?.label ?? assetTypeId;
}

// ─────────────────────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────────────────────

export function useSelfFundingForm(
  assetTypeId: AssetTypeId,
  fundingSourceId: string | null,
  existingSource?: SelfFundingSource,
) {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const activeFundingSourceOperation = useSelector(selectActiveFundingSourceOperation);

  const clientId = client.clientId;
  const inference = client.appliedInferenceSet;
  const ltcAtYear =
    inference?.ltcAtYear ??
    (inference?.yearsTillLtc != null
      ? new Date().getFullYear() + Math.max(0, Math.round(inference.yearsTillLtc))
      : new Date().getFullYear() + 30);
  const ltcAtAge = inference?.ltcAtAge ?? null;
  const currentAge =
    ltcAtAge != null && inference?.yearsTillLtc != null && inference.yearsTillLtc > 0
      ? Math.max(0, Math.round(ltcAtAge - inference.yearsTillLtc))
      : ltcAtAge;

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const isIncomeStream = isIncomeStreamAssetTypeId(assetTypeId);

  const careHasStarted = useCareHasStarted();

  const dispatch = useDispatch();

  // Track if this specific funding source is being saved
  const isSaving =
    activeFundingSourceOperation !== null &&
    activeFundingSourceOperation.fundingSourceId === fundingSourceId;

  const defaultFormValues = () => ({
    existingAssetsValue: existingSource?.existingAssetsValue ?? undefined,
    yearlyContribution: isIncomeStream
      ? null
      : existingSource?.monthlyContributionAmount
        ? existingSource.monthlyContributionAmount * 12
        : careHasStarted
          ? 0
          : null,
    annualRateOfReturn: isIncomeStream
      ? (existingSource?.annualRateOfReturn ?? 0)
      : (existingSource?.annualRateOfReturn ?? (careHasStarted ? 0 : undefined)),
    gainsTaxRate: existingSource?.gainsTaxRate ?? (careHasStarted ? 0 : undefined),
    contributionStopYear: existingSource?.contributionStopYear ?? null,
    hasDrawdown: !!existingSource?.hasDrawdown,
    drawdownStartAtYear: existingSource?.drawdownStartAtYear ?? null,
    drawdownEndAtYear: existingSource?.drawdownEndAtYear ?? null,
    drawdownAmount: existingSource?.drawdownAmount ?? null,
    drawdownAmountInterval: existingSource?.drawdownAmountInterval ?? 'yearly',
    incomeReferenceAge: isIncomeStream
      ? (existingSource?.incomeReferenceAge ?? ltcAtAge ?? null)
      : null,
  });

  const form = useForm<SelfFundingFormInput, unknown, SelfFundingFormValues>({
    resolver: zodResolver(selfFundingFormSchema),
    mode: 'onSubmit',
    defaultValues: defaultFormValues(),
  });

  // Reset form when fundingSourceId or existingSource changes
  useEffect(() => {
    form.reset(defaultFormValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fundingSourceId, existingSource]);

  // Seed incomeReferenceAge once client inference (ltcAtAge) becomes available.
  // Handles the race where the form mounts before client data loads.
  useEffect(() => {
    if (!isIncomeStream) return;
    if (ltcAtAge == null) return;
    if (form.getValues('incomeReferenceAge') != null) return;
    form.setValue('incomeReferenceAge', ltcAtAge);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncomeStream, ltcAtAge]);

  const buildSourceData = useCallback(
    (data: SelfFundingFormValues): SelfFundingSource => {
      const accountType = ASSET_TYPE_TO_ACCOUNT_TYPE[assetTypeId];
      const accountName = getAssetLabel(assetTypeId);

      // Preserve partner-link metadata from the existing source so downstream
      // consumers (applyCardAllocation's unlink check, Redux PUT) can still see
      // that the source is mirrored. Without this, sourceData.partnerAction is
      // undefined and unlink-from-guided-planning silently no-ops.
      const linkMetadata: Partial<SelfFundingSource> = existingSource
        ? {
            partnerAction: existingSource.partnerAction,
            fundingSourceGroupId: existingSource.fundingSourceGroupId,
            splitRatio: existingSource.splitRatio,
            addedByClientId: existingSource.addedByClientId,
          }
        : {};

      if (isIncomeStream) {
        // Income stream (retirement income / pension / social security):
        //   existingAssetsValue = annual income at incomeReferenceAge,
        //   annualRateOfReturn  = expected annual income growth,
        //   gainsTaxRate        = income tax rate.
        // No contribution / drawdown fields.
        return {
          hasSelfFunding: true,
          selfFundingName: existingSource?.selfFundingName ?? accountName,
          accountType,
          existingAssetsValue: data.existingAssetsValue,
          annualRateOfReturn: data.annualRateOfReturn ?? 0,
          incomeReferenceAge: data.incomeReferenceAge ?? ltcAtAge ?? null,
          contributionStartYear: new Date().getFullYear(),
          contributionEndYear: ltcAtYear,
          monthlyContributionAmount: 0,
          gainsTaxRate: data.gainsTaxRate,
          monthlyIncome: null,
          contributionMonthCount: 0,
          ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
          ...linkMetadata,
        } as SelfFundingSource;
      }

      return {
        hasSelfFunding: true,
        selfFundingName: existingSource?.selfFundingName ?? accountName,
        accountType,
        existingAssetsValue: data.existingAssetsValue,
        annualRateOfReturn: data.annualRateOfReturn,
        contributionStartYear: new Date().getFullYear(),
        contributionEndYear: ltcAtYear,
        monthlyContributionAmount: (data.yearlyContribution ?? 0) / 12,
        gainsTaxRate: data.gainsTaxRate,
        monthlyIncome: null,
        contributionMonthCount: 0,
        contributionStopYear: data.contributionStopYear ?? null,
        hasDrawdown: !!data.hasDrawdown,
        drawdownStartAtYear: data.hasDrawdown ? (data.drawdownStartAtYear ?? null) : null,
        drawdownEndAtYear: data.hasDrawdown ? (data.drawdownEndAtYear ?? null) : null,
        drawdownAmount: data.hasDrawdown ? (data.drawdownAmount ?? null) : null,
        drawdownAmountInterval: data.hasDrawdown ? (data.drawdownAmountInterval ?? 'yearly') : null,
        ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
        ...linkMetadata,
      } as SelfFundingSource;
    },
    [
      assetTypeId,
      isIncomeStream,
      existingSource?.selfFundingName,
      existingSource?.partnerAction,
      existingSource?.fundingSourceGroupId,
      existingSource?.splitRatio,
      existingSource?.addedByClientId,
      ltcAtYear,
      ltcAtAge,
      currentAge,
      isAdvisorSession,
      advisorId,
    ],
  );

  const onSubmit = useCallback(
    (data: SelfFundingFormValues) => {
      const updatedSelfFunding = buildSourceData(data);

      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId,
          fundingSourceType: 'selfFunding',
          fundingSourceData: updatedSelfFunding,
          fundingSourceId: fundingSourceId ?? null,
        }),
      );
    },
    [buildSourceData, clientId, fundingSourceId, dispatch],
  );

  const allPhaseCareMonthsNeeded = client?.allPhaseCosts?.allPhaseCareMonthsNeeded;
  const ltcEndAge =
    ltcAtAge != null && allPhaseCareMonthsNeeded != null && allPhaseCareMonthsNeeded > 0
      ? ltcAtAge + Math.floor(allPhaseCareMonthsNeeded / 12)
      : ltcAtAge;

  return {
    form,
    onSubmit,
    buildSourceData,
    isSaving,
    clientId,
    isIncomeStream,
    ltcAtAge,
    currentAge,
    ltcEndAge,
    careHasStarted,
  };
}
